import type { Call } from '../types';

/**
 * CallRail returns two different shapes in a call's `source` field.
 *
 *  - A human-readable source name, e.g. "Google Ads" or
 *    "SEO: Amada Senior Care - Tulsa, OK". These rows also carry `sourceName`
 *    (the tracker's own name) and leave `campaign` null.
 *  - A raw lowercase source-*type* slug, e.g. `google_paid`, `landing_url`,
 *    `print_ad` or `disabled`. These rows leave `sourceName` null and put the
 *    tracker name in `campaign` instead.
 *
 * We stored and rendered whichever one arrived, so ~17% of calls displayed the
 * literal API token — most visibly `disabled`, CallRail's value for a number
 * with no online source tracking (a direct dial to a per-AF SEO number from a
 * GBP listing, a directory or a saved contact). See issue #134.
 *
 * `campaign` is deliberately NOT used as the label: it is the per-AF tracker
 * name ("AF-123.2 Google Ads - Central Houston"), so substituting it would
 * shatter every bucket into hundreds of one-off labels. The slug is the source
 * type, so the slug is what we humanize.
 */
const SLUG_LABELS: Record<string, string> = {
  // No online source to attribute — the same thing the empty-source fallback
  // has always called "Direct", so it deliberately lands in that same bucket.
  disabled: 'Direct',
  google_paid: 'Google Paid',
  google_organic: 'Google Organic',
  google_ad_extension: 'Google Ad Extension',
  facebook_paid: 'Facebook Paid',
  bing_paid: 'Bing Paid',
  bing_organic: 'Bing Organic',
  landing_url: 'Landing Page',
  print_ad: 'Print Ad',
  direct: 'Direct',
  offline: 'Offline',
  // Tracker that accepts traffic from any source rather than a specific one.
  all: 'All Sources',
};

/** A CallRail source-type token rather than a human-readable source name. */
function isSlug(source: string): boolean {
  return /^[a-z0-9]+(_[a-z0-9]+)*$/.test(source);
}

/** Title-case an unrecognized slug so a new CallRail type still reads cleanly. */
function humanizeSlug(slug: string): string {
  return slug
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * The label to show for a call's source.
 *
 * `resolvedSource` is the tracker the call's number belongs to, filled in by the
 * server for rows whose CallRail source says nothing (`disabled` and friends).
 * It wins where present: those numbers are per-AF SEO numbers published only
 * online, so "Direct" would misattribute organic search as untracked walk-up
 * traffic. Informative slugs (`google_paid`, `landing_url`) keep their own
 * meaning, and an unrecognised number falls back to labeling the slug.
 */
export function callSourceLabel(c: Pick<Call, 'source' | 'resolvedSource'>): string {
  const resolved = c.resolvedSource?.trim();
  if (resolved) return resolved;
  const source = c.source?.trim();
  if (!source) return 'Direct';
  if (!isSlug(source)) return source;
  return SLUG_LABELS[source] ?? humanizeSlug(source);
}

/**
 * The tracking number a call arrived on, shown under the source in the drawer.
 *
 * This used to fall back to `campaign`, which looked like the tracker name but
 * disagreed with its own number on ~10% of rows (a Birmingham number labeled
 * Tulsa). The dialed number is a fact on every row, so we show that instead.
 */
export function callTrackingNumber(c: Pick<Call, 'trackingPhoneNumber'>): string | null {
  const n = c.trackingPhoneNumber?.trim();
  if (!n) return null;
  const m = n.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
}
